import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { reviews, reviewStats } from "@/mocks/reviews";

function StarRating({ rating, size = "md" }: { rating: number; size?: "sm" | "md" | "lg" }) {
  const sizeClass = size === "sm" ? "text-sm" : size === "lg" ? "text-2xl" : "text-lg";
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <i
          key={i}
          className={`${i < rating ? "ri-star-fill text-[#c9a98a]" : "ri-star-line text-[#d9cfc6]"} ${sizeClass}`}
        />
      ))}
    </div>
  );
}

function ReviewCard({ review, index }: { review: (typeof reviews)[0]; index: number }) {
  const [imgVisible, setImgVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setImgVisible(true), index * 80);
    return () => clearTimeout(timer);
  }, [index]);

  return (
    <article
      className={`bg-white rounded-lg overflow-hidden transition-all duration-700 ${imgVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
    >
      {/* Outfit Image */}
      <div className="relative w-full aspect-[4/5] overflow-hidden">
        <img
          src={review.outfitImage}
          alt={`${review.name}穿搭心得照 - ${review.purchasedItem}`}
          className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full border border-white/30">
            {review.purchasedItem}
          </span>
        </div>
        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full px-2.5 py-1 flex items-center gap-1">
          <i className="ri-star-fill text-[#c9a98a] text-xs"></i>
          <span className="text-[#3d2b1f] text-xs font-bold">{review.rating}.0</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
              <img
                src={review.avatar}
                alt={review.name}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <p className="text-[#3d2b1f] text-sm font-semibold">{review.name}</p>
              <p className="text-[#a09080] text-xs">{review.age} · {review.location}</p>
            </div>
          </div>
          <StarRating rating={review.rating} size="sm" />
        </div>

        <p className="text-[#8B5E52] text-sm font-semibold mb-2 leading-snug">{review.highlight}</p>

        <p className="text-[#7a6455] text-sm leading-relaxed line-clamp-3 mb-3">
          {review.comment}
        </p>

        <div className="flex flex-wrap gap-1.5">
          {review.tags.map((tag) => (
            <span
              key={tag}
              className="text-xs text-[#8B5E52] bg-[#f5ede4] px-2.5 py-1 rounded-full"
            >
              #{tag}
            </span>
          ))}
        </div>

        <p className="text-[#c0b0a0] text-xs mt-3">{review.date.replace(/-/g, " / ")}</p>
      </div>
    </article>
  );
}

export default function Reviews() {
  const [activeCategory, setActiveCategory] = useState("全部");

  const filtered = activeCategory === "全部"
    ? reviews
    : reviews.filter((r) => r.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative h-[56vh] min-h-[400px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=elegant%20taiwanese%20women%20fashion%20boutique%20happy%20customers%20trying%20on%20clothes%20warm%20inviting%20interior%20cream%20beige%20rose%20tones%20designer%20fashion%20quality%20graceful%20atmosphere%20soft%20warm%20lighting&width=1400&height=700&seq=reviews_hero001&orientation=landscape"
            alt="顧客好評"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/50" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <p className="text-[#f5d8b8] text-sm tracking-[0.3em] uppercase font-medium mb-3">Real Reviews</p>
            <h1 className="text-white text-[2.75rem] md:text-[3.25rem] font-bold leading-[1.3] mb-4">顧客真實好評</h1>
            <p className="text-white/80 text-lg max-w-xl leading-relaxed">
              每一張穿搭照片，都是真實顧客的自信與喜悅。感謝她們願意分享，讓更多女性相信自己可以穿得更美。
            </p>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-[#3d2b1f] py-10">
          <div className="w-full px-6 md:px-16 max-w-[1400px] mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <p className="text-[#c9a98a] text-[2.5rem] font-bold leading-none mb-1">{reviewStats.totalReviews}+</p>
                <p className="text-[#f5e6dc]/70 text-sm tracking-wide">累積好評數</p>
              </div>
              <div>
                <p className="text-[#c9a98a] text-[2.5rem] font-bold leading-none mb-1">{reviewStats.averageRating}</p>
                <p className="text-[#f5e6dc]/70 text-sm tracking-wide">平均評分</p>
              </div>
              <div>
                <p className="text-[#c9a98a] text-[2.5rem] font-bold leading-none mb-1">{reviewStats.fiveStarPercent}%</p>
                <p className="text-[#f5e6dc]/70 text-sm tracking-wide">五星好評率</p>
              </div>
              <div>
                <p className="text-[#c9a98a] text-[2.5rem] font-bold leading-none mb-1">{reviewStats.repeatCustomerPercent}%</p>
                <p className="text-[#f5e6dc]/70 text-sm tracking-wide">回購顧客比例</p>
              </div>
            </div>
          </div>
        </section>

        {/* Filter + Reviews Grid */}
        <section className="py-16 md:py-24 bg-[#faf8f5]">
          <div className="w-full px-6 md:px-16 max-w-[1400px] mx-auto">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 mb-10">
              {/* Category Filter */}
              <div className="flex flex-wrap gap-2">
                {reviewStats.categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => setActiveCategory(cat.name)}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      activeCategory === cat.name
                        ? "bg-[#8B5E52] text-white"
                        : "bg-white text-[#7a6455] border border-[#e0d4c8] hover:border-[#8B5E52] hover:text-[#8B5E52]"
                    }`}
                  >
                    {cat.name}
                    <span className={`ml-1.5 text-xs ${activeCategory === cat.name ? "text-white/70" : "text-[#a09080]"}`}>
                      ({cat.count})
                    </span>
                  </button>
                ))}
              </div>

              {/* Count Label */}
              <p className="text-[#7a6455] text-sm flex-shrink-0">
                共 <strong className="text-[#8B5E52] text-base">{filtered.length}</strong> 則
                {activeCategory !== "全部" ? `「${activeCategory}」` : ""}好評
              </p>
            </div>

            {/* Reviews Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filtered.map((review, i) => (
                <ReviewCard key={review.id} review={review} index={i} />
              ))}
            </div>
          </div>
        </section>

        {/* Featured Review - Full Width */}
        <section className="py-20 md:py-28 bg-[#f5ede4]">
          <div className="w-full px-6 md:px-16 max-w-[1400px] mx-auto">
            <div className="text-center mb-14">
              <p className="text-[#8B5E52] text-sm tracking-[0.25em] uppercase font-medium mb-3">Featured Story</p>
              <h2 className="text-[#3d2b1f] text-[2.125rem] md:text-[2.5rem] font-bold leading-[1.4]">精選好評故事</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center bg-white rounded-lg overflow-hidden">
              <div className="relative aspect-[4/5] lg:aspect-auto lg:h-full min-h-[400px] overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=elegant%20mature%20taiwanese%20woman%2050s%20wearing%20refined%20outfit%20boutique%20interior%20trying%20on%20clothes%20happy%20satisfied%20customer%20warm%20cream%20rose%20beige%20tones%20designer%20fashion%20quality%20graceful&width=700&height=800&seq=reviews_featured001&orientation=portrait"
                  alt="精選顧客好評故事 - 台中女裝Outlet真實穿搭心得"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-sm rounded-lg px-4 py-3">
                  <StarRating rating={5} size="md" />
                </div>
              </div>
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#f5e8d8] mb-6">
                  <i className="ri-double-quotes-l text-[#8B5E52] text-2xl"></i>
                </div>
                <blockquote className="text-[#3d2b1f] text-xl md:text-2xl font-medium leading-relaxed mb-6 italic">
                  「我 50 多歲了，曾經以為時尚離我越來越遠。直到走進這裡，才發現原來穿對了衣服，任何年紀都可以很美，而且比年輕時更有味道。」
                </blockquote>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 flex-shrink-0 rounded-full overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=portrait%20mature%20elegant%20taiwanese%20woman%2054%20warm%20confident%20smile%20cream%20background%20sophisticated%20graceful%20dignified%20beauty&width=140&height=140&seq=rv_featured_av001&orientation=squarish"
                      alt="張雅芳"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div>
                    <p className="text-[#3d2b1f] font-semibold">張雅芳</p>
                    <p className="text-[#a09080] text-sm">54歲 · 台中市中區 · 回購顧客</p>
                  </div>
                </div>
                <p className="text-[#7a6455] text-base leading-relaxed mb-8">
                  張女士是我們三年的老顧客，累積購買超過 20 件單品。她說穿對衣服讓她重新愛上鏡子裡的自己，連走路的姿態都變了。現在她已經是朋友圈中的穿搭達人，總是帶著新朋友一起來採購。
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/appointment"
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-[#8B5E52] text-white rounded-full text-sm font-semibold hover:bg-[#7a4f44] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-calendar-check-line"></i>
                    預約試穿
                  </Link>
                  <Link
                    to="/shop"
                    className="flex items-center justify-center gap-2 px-6 py-3 border border-[#8B5E52] text-[#8B5E52] rounded-full text-sm font-semibold hover:bg-[#8B5E52] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-shopping-bag-line"></i>
                    瀏覽商品
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 text-center px-6 bg-[#faf8f5]">
          <div className="max-w-xl mx-auto">
            <p className="text-[#8B5E52] text-sm tracking-[0.25em] uppercase font-medium mb-4">Join Our Community</p>
            <h2 className="text-[#3d2b1f] text-[2rem] md:text-[2.375rem] font-bold mb-4 leading-[1.4]">成為下一位分享好評的她</h2>
            <p className="text-[#7a6455] text-lg mb-8 leading-relaxed">
              現在預約試穿，讓我們為您找到最適合的款式。每一件衣服都可能是您生命中的那件好衣服。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/appointment"
                className="px-8 py-3.5 bg-[#8B5E52] text-white rounded-full text-base font-bold hover:bg-[#7a4f44] transition-colors cursor-pointer whitespace-nowrap"
              >
                立即預約試穿
              </Link>
              <a
                href="https://line.me"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-8 py-3.5 border-2 border-[#3d2b1f] text-[#3d2b1f] rounded-full text-base font-semibold hover:bg-[#3d2b1f] hover:text-[#faf8f5] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-line-fill"></i>
                加入 LINE 詢問
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
